import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, FileText, Download, ArrowRight } from "lucide-react";
import { OrderStatus, ProcessingType, UIOrder, OrderItemType } from "@/models";

const mockConfirmedOrders: Record<string, UIOrder> = {
  "order-123": {
    id: 123,
    customer_id: 1,
    association_id: 1,
    property_id: 1,
    processing_type: ProcessingType.Normal,
    status: OrderStatus.approved,
    total_amount: 124.99,
    created_at: new Date(),
    updated_at: new Date(),
    associationName: "Sunset Condominiums",
    items: [
      { 
        id: 1, 
        order_id: 123,
        item_type: OrderItemType.document_fee, 
        reference_id: 1, 
        label: "Resale Certificate", 
        amount: 75,
        quantity: 1,
        subtotal_amount: 75,
        created_at: new Date() 
      },
      { 
        id: 2, 
        order_id: 123,
        item_type: OrderItemType.document_fee, 
        reference_id: 3, 
        label: "Bylaws & CC&Rs", 
        amount: 45,
        quantity: 1,
        subtotal_amount: 45,
        created_at: new Date() 
      },
    ],
    downloadLinks: [
      { id: "dl-001", name: "Resale Certificate.pdf", docId: "doc-001" },
      { id: "dl-002", name: "Bylaws and CCRs.pdf", docId: "doc-003" },
    ],
  },
  "order-456": {
    id: 456,
    customer_id: 2,
    association_id: 2,
    property_id: 2,
    processing_type: ProcessingType.Rush,
    status: OrderStatus.approved,
    total_amount: 129.99,
    created_at: new Date(),
    updated_at: new Date(),
    associationName: "Highland Heights HOA",
    items: [
      { 
        id: 3, 
        order_id: 456,
        item_type: OrderItemType.document_fee, 
        reference_id: 5, 
        label: "Resale Package", 
        amount: 125,
        quantity: 1,
        subtotal_amount: 125,
        created_at: new Date() 
      },
    ],
    downloadLinks: [
      { id: "dl-003", name: "Resale Package.pdf", docId: "doc-005" },
    ],
  },
};

const Confirmation = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<UIOrder | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      if (orderId && mockConfirmedOrders[orderId]) {
        setOrder(mockConfirmedOrders[orderId]);
      } else {
        const defaultOrder: UIOrder = {
          id: Number(orderId) || Math.floor(Math.random() * 1000),
          customer_id: 1,
          association_id: 1,
          property_id: 1,
          processing_type: ProcessingType.Normal,
          status: OrderStatus.approved,
          total_amount: 79.99,
          created_at: new Date(),
          updated_at: new Date(),
          associationName: "Sample Association",
          items: [
            { 
              id: 1, 
              order_id: 1,
              item_type: OrderItemType.document_fee, 
              reference_id: 1, 
              label: "Sample Document", 
              amount: 75,
              quantity: 1,
              subtotal_amount: 75,
              created_at: new Date() 
            },
          ],
          downloadLinks: [
            { id: "dl-001", name: "Sample Document.pdf", docId: "doc-001" },
          ],
        };
        setOrder(defaultOrder);
      }
      setLoading(false);
    }, 1000);
  }, [orderId]);

  const handleDownload = (docId: string) => {
    // Note: This is a mock page, so this is a placeholder implementation
    // In a real implementation, this would download the actual document
    console.log(`Downloading document ${docId}`);
    
    // For demo purposes, show a message that this is a mock
    alert(`This is a mock confirmation page. In a real implementation, document ${docId} would be downloaded.`);
  };

  if (loading) {
    return (
      <BuyerLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </BuyerLayout>
    );
  }

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Order Confirmation</h1>
          <p className="text-gray-500">
            Your order has been successfully placed
          </p>
        </div>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-green-700 mb-2">Thank You for Your Order</h2>
            <p className="text-green-600">
              Your payment has been processed successfully
            </p>
            <div className="mt-2 text-sm text-green-600">
              Order #{order?.id}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
            <CardDescription>
              {order?.created_at.toLocaleDateString()} at {order?.created_at.toLocaleTimeString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Association</h3>
                <p className="text-sm">{order?.associationName}</p>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Documents</h3>
                <ul className="space-y-2">
                  {order?.items?.map((item) => (
                    <li key={item.id} className="flex justify-between text-sm">
                      <span>{item.label}</span>
                      <span className="font-medium">${item.amount?.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between font-bold mt-4 pt-4 border-t">
                  <span>Total</span>
                  <span>${order?.total_amount?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>
              Your documents are ready for download
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {order?.downloadLinks.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-md">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-brand-blue mr-3" />
                    <span>{doc.name}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDownload(doc.docId)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between flex-wrap gap-4">
            <Button variant="outline" asChild>
              <Link to="/buyer/dashboard">
                Go to Dashboard
              </Link>
            </Button>
            <Button asChild>
              <Link to="/buyer/orders">
                View All Orders
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </BuyerLayout>
  );
};

export default Confirmation;
